import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  TrashIcon,
  StarIcon,
  DocumentTextIcon,
  UserGroupIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import { CanView } from '@/components/auth/CanView';
import { usePermission } from '@/hooks/usePermission';
import { cn } from '@/utils/cn';
import {
  securityVendorService,
  type SecurityVendor,
  type SecurityVendorStatus,
  type SecurityVendorReminderType,
  type ReviewInput,
} from '@/services/securityVendor.service';
import VendorFormModal from '@/components/security-vendors/VendorFormModal';
import {
  DOMAIN_LABELS,
  REGION_LABELS,
  CERTIFICATION_LABELS,
  TIER_LABELS,
  STATUS_LABELS,
  STATUS_PILL,
  CONTRACT_STATUS_LABELS,
  REVIEW_CADENCE_LABELS,
  nextStatuses,
  isVerifiedRecommended,
  canVerifyRecommended,
} from '@/components/security-vendors/vendorOptions';

type TabKey = 'overview' | 'lifecycle' | 'reviews' | 'contacts' | 'contracts' | 'activity';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'lifecycle', label: 'Lifecycle' },
  { key: 'reviews', label: 'Ratings & Reviews' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'contracts', label: 'Contracts' },
  { key: 'activity', label: 'Activity Log' },
];

const REMINDER_TYPE_LABELS: Record<SecurityVendorReminderType, string> = {
  CONTRACT_EXPIRATION: 'Contract expiry',
  CERTIFICATION_EXPIRATION: 'Certification expiry',
  PERIODIC_REVIEW: 'Periodic review',
};

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-secondary-400">{label}</dt>
      <dd className="mt-0.5 text-sm text-secondary-800 dark:text-secondary-200">{value || '—'}</dd>
    </div>
  );
}

export function formatDate(value?: string | null): string {
  if (!value) {return '—';}
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {return '—';}
  // Contract/expiry dates are date-only values persisted as UTC midnight. Rendering those in
  // a negative-offset local zone shifts them a day back (a 2026-02-01 start date displayed as
  // 1/31/2026), so pin midnight-UTC values to UTC. Real timestamps stay local.
  const isUtcMidnight = d.getTime() % 86_400_000 === 0;
  return d.toLocaleDateString(undefined, isUtcMidnight ? { timeZone: 'UTC' } : undefined);
}

/**
 * Only render a vendor-supplied URL as a link if it's http(s) — `z.string().url()`
 * accepts `javascript:`/`data:` schemes, which would otherwise be a stored-XSS click sink.
 */
function safeHttpUrl(value?: string | null): string | undefined {
  if (!value) {return undefined;}
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:' ? value : undefined;
  } catch {
    return undefined;
  }
}

export default function SecurityVendorDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TabKey>('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const { data: vendor, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['security-vendor', id],
    queryFn: () => securityVendorService.get(id),
    enabled: Boolean(id),
  });

  const deleteMutation = useMutation({
    mutationFn: () => securityVendorService.remove(id),
    onSuccess: () => {
      toast.success('Vendor deleted');
      // Drop the cached directory list so the soft-deleted vendor doesn't linger
      // on the list after we navigate back.
      void queryClient.invalidateQueries({ queryKey: ['security-vendors'] });
      navigate('/security/vendor-directory');
    },
    // L-019: surface delete failures (e.g. 403 role gate) to the user.
    onError: (e: unknown) => {
      toast.error(e instanceof Error ? e.message : 'Failed to delete vendor');
      setShowDelete(false);
    },
  });

  if (isLoading) {
    return <div className="p-6 text-center text-secondary-500">Loading vendor…</div>;
  }
  if (isError || !vendor) {
    return (
      <div className="p-6">
        <Button variant="outline" leftIcon={<ArrowLeftIcon className="h-4 w-4" />} onClick={() => navigate('/security/vendor-directory')}>
          Back
        </Button>
        <p className="mt-4 text-danger-600">{error instanceof Error ? error.message : 'Vendor not found'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <Button variant="outline" size="icon-sm" aria-label="Back" onClick={() => navigate('/security/vendor-directory')}>
            <ArrowLeftIcon className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">{vendor.name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', STATUS_PILL[vendor.status])}>
                {STATUS_LABELS[vendor.status]}
              </span>
              {vendor.vendorTier && (
                <span className="text-xs text-secondary-500">{TIER_LABELS[vendor.vendorTier]}</span>
              )}
              <span className="flex items-center gap-1 text-sm text-secondary-600 dark:text-secondary-300">
                <StarIcon className="h-4 w-4 text-amber-400" />
                {vendor.compositeRating > 0 ? vendor.compositeRating.toFixed(1) : '—'}
                <span className="text-xs text-secondary-400">({vendor.reviewCount} reviews)</span>
              </span>
              {isVerifiedRecommended(vendor) && (
                <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                  <CheckBadgeIcon className="h-4 w-4" /> Verified Recommended
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <CanView permission="security_vendors:update">
            <Button variant="outline" leftIcon={<PencilSquareIcon className="h-4 w-4" />} onClick={() => setShowEdit(true)}>
              Edit
            </Button>
          </CanView>
          <CanView permission="security_vendors:delete">
            <Button variant="destructive" leftIcon={<TrashIcon className="h-4 w-4" />} onClick={() => setShowDelete(true)}>
              Delete
            </Button>
          </CanView>
        </div>
      </div>

      <div className="border-b border-secondary-200 dark:border-secondary-700">
        <nav className="-mb-px flex gap-6 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                'whitespace-nowrap border-b-2 py-2 text-sm font-medium',
                tab === t.key
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 dark:hover:text-secondary-300'
              )}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'overview' && <OverviewTab vendor={vendor} />}
      {tab === 'lifecycle' && <LifecycleTab vendor={vendor} />}
      {tab === 'reviews' && <ReviewsTab vendor={vendor} />}
      {tab === 'contacts' && <ContactsTab vendor={vendor} />}
      {tab === 'contracts' && <ContractsTab vendor={vendor} />}
      {tab === 'activity' && <ActivityTab vendor={vendor} />}

      {showEdit && (
        <VendorFormModal
          vendor={vendor}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); void refetch(); }}
        />
      )}

      <ConfirmationDialog
        isOpen={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete vendor"
        message={`Remove "${vendor.name}" from the directory? This is a soft-delete and can be restored by an administrator.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
}

function OverviewTab({ vendor }: { vendor: SecurityVendor }) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardContent className="space-y-4 p-6">
          <div>
            <h3 className="mb-1 flex items-center gap-2 font-semibold text-secondary-900 dark:text-white">
              <DocumentTextIcon className="h-5 w-5 text-secondary-400" /> About
            </h3>
            <p className="text-sm text-secondary-600 dark:text-secondary-300">{vendor.description || 'No description provided.'}</p>
          </div>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Website" value={safeHttpUrl(vendor.websiteUrl) ? <a href={safeHttpUrl(vendor.websiteUrl)} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">{vendor.websiteUrl}</a> : vendor.websiteUrl || undefined} />
            <Field label="Tier" value={vendor.vendorTier ? TIER_LABELS[vendor.vendorTier] : undefined} />
            <Field label="Countries" value={vendor.countries.length ? vendor.countries.join(', ') : undefined} />
          </dl>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="space-y-4 p-6">
          <div>
            <dt className="mb-2 text-xs font-medium uppercase tracking-wide text-secondary-400">Domains</dt>
            <div className="flex flex-wrap gap-1.5">
              {vendor.domains.length ? vendor.domains.map((d) => (
                <span key={d} className="rounded bg-primary-50 px-2 py-0.5 text-xs text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">{DOMAIN_LABELS[d]}</span>
              )) : <span className="text-sm text-secondary-400">—</span>}
            </div>
          </div>
          <div>
            <dt className="mb-2 text-xs font-medium uppercase tracking-wide text-secondary-400">Regions</dt>
            <div className="flex flex-wrap gap-1.5">
              {vendor.regions.length ? vendor.regions.map((r) => (
                <span key={r} className="rounded-full border border-secondary-200 px-2 py-0.5 text-xs text-secondary-500 dark:border-secondary-700">{REGION_LABELS[r]}</span>
              )) : <span className="text-sm text-secondary-400">—</span>}
            </div>
          </div>
          <div>
            <dt className="mb-2 text-xs font-medium uppercase tracking-wide text-secondary-400">Certifications</dt>
            <div className="flex flex-wrap gap-1.5">
              {vendor.certifications.length ? vendor.certifications.map((c) => (
                <span key={c} className="rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">{CERTIFICATION_LABELS[c]}</span>
              )) : <span className="text-sm text-secondary-400">—</span>}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * LifecycleTab (VIS-203) — enforced status transitions, expiry/review reminders,
 * and the audit trail. Transition buttons only offer legal next states (FSM mirror
 * of the backend), so the UI can never send an illegal transition (L-032).
 */
export function LifecycleTab({ vendor }: { vendor: SecurityVendor }) {
  const queryClient = useQueryClient();
  // Blacklisting is the ADMIN+ destructive cell of the VIS-30 matrix (same as delete);
  // the backend role-gates the BLACKLISTED transition even though the status endpoint is
  // `update`-gated. Offer it only to callers who can also delete, so a Security Manager
  // isn't shown a button the backend 403s (L-032).
  const canBlacklist = usePermission('security_vendors:delete');
  // RECOMMENDED is reached only through the verify flow (Ratings & Reviews tab), which
  // enforces the review threshold + verifier stamp — the backend 409s a manual status
  // move to RECOMMENDED (RECOMMENDED_VERIFY_ONLY / L-110). Drop it from the manual
  // transition buttons so the UI never offers a path the backend rejects (L-032).
  const transitions = nextStatuses(vendor.status).filter(
    (s) => s !== 'RECOMMENDED' && (s !== 'BLACKLISTED' || canBlacklist)
  );

  const { data: reminders } = useQuery({
    queryKey: ['security-vendor-reminders', vendor.id],
    queryFn: () => securityVendorService.getReminders(vendor.id),
  });
  const { data: audit } = useQuery({
    queryKey: ['security-vendor-audit', vendor.id],
    queryFn: () => securityVendorService.getAudit(vendor.id, { limit: 50 }),
  });

  const statusMutation = useMutation({
    mutationFn: (to: SecurityVendorStatus) => securityVendorService.updateStatus(vendor.id, to),
    onSuccess: (updated) => {
      toast.success(`Status changed to ${STATUS_LABELS[updated.status]}`);
      void queryClient.invalidateQueries({ queryKey: ['security-vendor', vendor.id] });
      void queryClient.invalidateQueries({ queryKey: ['security-vendor-audit', vendor.id] });
    },
    // L-019: surface transition failures (e.g. 403, illegal transition) to the user.
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to update status'),
  });

  const scheduled = (reminders ?? []).filter((r) => r.status === 'SCHEDULED');
  const auditEntries = audit?.entries ?? [];

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <Card>
          <CardContent className="space-y-4 p-6">
            <h3 className="font-semibold text-secondary-900 dark:text-white">Lifecycle status</h3>
            <div className="flex items-center gap-2">
              <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', STATUS_PILL[vendor.status])}>
                {STATUS_LABELS[vendor.status]}
              </span>
            </div>
            <CanView permission="security_vendors:update">
              {transitions.length === 0 ? (
                <p className="text-sm text-secondary-400">No further transitions from this status.</p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="lifecycle-transitions">
                  {transitions.map((to) => (
                    <Button
                      key={to}
                      variant="outline"
                      size="sm"
                      disabled={statusMutation.isPending}
                      onClick={() => statusMutation.mutate(to)}
                    >
                      Move to {STATUS_LABELS[to]}
                    </Button>
                  ))}
                </div>
              )}
            </CanView>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <h3 className="mb-3 font-semibold text-secondary-900 dark:text-white">Audit trail</h3>
            {auditEntries.length === 0 ? (
              <p className="text-sm text-secondary-500">No changes recorded yet.</p>
            ) : (
              <ul className="divide-y divide-secondary-100 dark:divide-secondary-800">
                {auditEntries.map((e) => (
                  <li key={e.id} className="flex items-start gap-3 py-2">
                    <DocumentTextIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-secondary-400" />
                    <div>
                      <p className="text-sm font-medium text-secondary-800 dark:text-secondary-200">{e.action}</p>
                      <p className="text-xs text-secondary-500">
                        {formatDate(e.createdAt)}{e.userId ? ` · by ${e.userName ?? e.userId}` : ''}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="space-y-4 p-6">
          <h3 className="font-semibold text-secondary-900 dark:text-white">Upcoming reminders</h3>
          <dl className="grid grid-cols-1 gap-3">
            <Field
              label="Review cadence"
              value={vendor.reviewCadence ? REVIEW_CADENCE_LABELS[vendor.reviewCadence] : undefined}
            />
            <Field label="Next review" value={formatDate(vendor.nextReviewDate)} />
            <Field label="Certification expiry" value={formatDate(vendor.certificationExpiryDate)} />
          </dl>
          {scheduled.length === 0 ? (
            <p className="text-sm text-secondary-500">No scheduled reminders.</p>
          ) : (
            <ul className="space-y-2" data-testid="lifecycle-reminders">
              {scheduled.map((r) => (
                <li key={r.id} className="text-sm">
                  <span className="font-medium text-secondary-800 dark:text-secondary-200">
                    {REMINDER_TYPE_LABELS[r.reminderType]}
                  </span>
                  <span className="text-secondary-500"> · {formatDate(r.scheduledDate)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Minimum reviews before a vendor can be Verified Recommended — mirrors
// MIN_REVIEWS_FOR_VERIFICATION in vendor-service/src/utils/vendor-rating.ts.
const MIN_REVIEWS_FOR_VERIFICATION = 3;

const REVIEW_CRITERIA: { key: keyof ReviewInput; label: string }[] = [
  { key: 'serviceQuality', label: 'Service quality' },
  { key: 'technicalCapability', label: 'Technical capability' },
  { key: 'compliancePosture', label: 'Compliance posture' },
  { key: 'responsiveness', label: 'Responsiveness' },
  { key: 'costEffectiveness', label: 'Cost effectiveness' },
];

const emptyReview: ReviewInput = {
  serviceQuality: 5,
  responsiveness: 5,
  costEffectiveness: 5,
  technicalCapability: 5,
  compliancePosture: 5,
  comments: '',
};

export function ReviewsTab({ vendor }: { vendor: SecurityVendor }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ReviewInput>(emptyReview);

  const { data: reviewsData } = useQuery({
    queryKey: ['security-vendor-reviews', vendor.id],
    queryFn: () => securityVendorService.listReviews(vendor.id, { limit: 50 }),
  });
  const { data: history } = useQuery({
    queryKey: ['security-vendor-rating-history', vendor.id],
    queryFn: () => securityVendorService.getRatingHistory(vendor.id),
  });
  const reviews = reviewsData?.reviews ?? vendor.reviews ?? [];

  const refreshVendorViews = () => {
    void queryClient.invalidateQueries({ queryKey: ['security-vendor', vendor.id] });
    void queryClient.invalidateQueries({ queryKey: ['security-vendor-reviews', vendor.id] });
    void queryClient.invalidateQueries({ queryKey: ['security-vendor-rating-history', vendor.id] });
  };

  const submitMutation = useMutation({
    mutationFn: () => securityVendorService.submitReview(vendor.id, form),
    onSuccess: () => {
      toast.success('Review submitted');
      setForm(emptyReview);
      refreshVendorViews();
    },
    // L-019: surface submit failures (e.g. 403 permission gate) to the user.
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to submit review'),
  });

  const verifyMutation = useMutation({
    mutationFn: () => securityVendorService.verify(vendor.id),
    onSuccess: () => {
      toast.success('Vendor marked Verified Recommended');
      refreshVendorViews();
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Failed to verify vendor'),
  });

  const belowThreshold = vendor.reviewCount < MIN_REVIEWS_FOR_VERIFICATION;
  const verified = isVerifiedRecommended(vendor);
  // Only offer verify when the current status can legally reach RECOMMENDED — otherwise
  // the button would 409 (verify enforces the same FSM). A stale stamp on a non-RECOMMENDED
  // vendor no longer reads as "Verified".
  const canVerify = canVerifyRecommended(vendor.status);
  const trend = (history ?? []).map((p) => ({
    date: new Date(p.date).toLocaleDateString(),
    composite: p.compositeAfter,
  }));

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <Card>
          <CardContent className="p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <StarIcon className="h-6 w-6 text-amber-400" />
                <span className="text-2xl font-bold text-secondary-900 dark:text-white">
                  {vendor.compositeRating > 0 ? vendor.compositeRating.toFixed(1) : '—'}
                </span>
                <span className="text-sm text-secondary-500">{vendor.reviewCount} review(s)</span>
              </div>
              <CanView permission="security_vendors:verify">
                {verified ? (
                  <span className="flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                    <CheckBadgeIcon className="h-5 w-5" /> Verified {formatDate(vendor.verifiedRecommendedAt)}
                  </span>
                ) : canVerify ? (
                  <Button
                    variant="outline"
                    leftIcon={<CheckBadgeIcon className="h-4 w-4" />}
                    disabled={belowThreshold || verifyMutation.isPending}
                    title={belowThreshold ? `Requires at least ${MIN_REVIEWS_FOR_VERIFICATION} reviews` : undefined}
                    onClick={() => verifyMutation.mutate()}
                  >
                    Mark Verified Recommended
                  </Button>
                ) : null}
              </CanView>
            </div>
            {belowThreshold && canVerify && !verified && (
              <p className="mb-4 text-xs text-secondary-400">
                {MIN_REVIEWS_FOR_VERIFICATION - vendor.reviewCount} more review(s) needed before this vendor
                can be Verified Recommended.
              </p>
            )}

            {trend.length > 1 && (
              <div className="mb-4 h-48" data-testid="rating-trend">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-secondary-200 dark:stroke-secondary-700" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 5]} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="composite" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {reviews.length === 0 ? (
              <p className="text-sm text-secondary-500">No reviews yet.</p>
            ) : (
              <ul className="divide-y divide-secondary-100 dark:divide-secondary-800">
                {reviews.map((r) => (
                  <li key={r.id} className="py-3">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 text-sm font-medium">
                        <StarIcon className="h-4 w-4 text-amber-400" />
                        {r.overallRating.toFixed(1)}
                      </span>
                      <span className="text-xs text-secondary-400">{formatDate(r.createdAt)}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-secondary-500">
                      {REVIEW_CRITERIA.map((c) => (
                        <span key={c.key}>{c.label}: {String(r[c.key as keyof typeof r] ?? '—')}</span>
                      ))}
                    </div>
                    {r.comments && <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-300">{r.comments}</p>}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <CanView permission="security_vendors:review">
        <Card>
          <CardContent className="space-y-4 p-6">
            <h3 className="font-semibold text-secondary-900 dark:text-white">Submit a review</h3>
            <form
              className="space-y-3"
              onSubmit={(e) => { e.preventDefault(); submitMutation.mutate(); }}
            >
              {REVIEW_CRITERIA.map((c) => (
                <label key={c.key} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-secondary-700 dark:text-secondary-300">{c.label}</span>
                  <select
                    aria-label={c.label}
                    className="rounded border border-secondary-300 bg-white px-2 py-1 text-sm dark:border-secondary-600 dark:bg-secondary-800"
                    value={form[c.key] as number}
                    onChange={(e) => setForm((f) => ({ ...f, [c.key]: Number(e.target.value) }))}
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>
              ))}
              <textarea
                aria-label="Comments"
                placeholder="Comments (optional)"
                className="w-full rounded border border-secondary-300 bg-white px-2 py-1 text-sm dark:border-secondary-600 dark:bg-secondary-800"
                rows={3}
                value={form.comments ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
              />
              <Button type="submit" className="w-full" disabled={submitMutation.isPending}>
                {submitMutation.isPending ? 'Submitting…' : 'Submit review'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </CanView>
    </div>
  );
}

function ContactsTab({ vendor }: { vendor: SecurityVendor }) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
      <Card>
        <CardContent className="space-y-3 p-6">
          <h3 className="flex items-center gap-2 font-semibold text-secondary-900 dark:text-white">
            <UserGroupIcon className="h-5 w-5 text-secondary-400" /> Primary Contact
          </h3>
          <dl className="space-y-3">
            <Field label="Name" value={vendor.primaryContactName} />
            <Field label="Email" value={vendor.primaryContactEmail} />
            <Field label="Phone" value={vendor.primaryContactPhone} />
          </dl>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="space-y-3 p-6">
          <h3 className="flex items-center gap-2 font-semibold text-secondary-900 dark:text-white">
            <UserGroupIcon className="h-5 w-5 text-secondary-400" /> Account Manager
          </h3>
          <dl className="space-y-3">
            <Field label="Name" value={vendor.accountManagerName} />
            <Field label="Email" value={vendor.accountManagerEmail} />
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}

function ContractsTab({ vendor }: { vendor: SecurityVendor }) {
  return (
    <Card>
      <CardContent className="space-y-4 p-6">
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Contract status" value={vendor.contractStatus ? CONTRACT_STATUS_LABELS[vendor.contractStatus] : undefined} />
          <Field label="Start date" value={formatDate(vendor.contractStartDate)} />
          <Field label="End date" value={formatDate(vendor.contractEndDate)} />
        </dl>
        <Field label="SLA summary" value={vendor.slaSummary} />
      </CardContent>
    </Card>
  );
}

function ActivityTab({ vendor }: { vendor: SecurityVendor }) {
  const events = [
    // Name where the server could resolve it; raw id only as a last resort (VIS-30 QA).
    { label: 'Created', at: vendor.createdAt, by: vendor.createdByName ?? vendor.createdBy },
    { label: 'Last updated', at: vendor.updatedAt, by: vendor.updatedByName ?? vendor.updatedBy },
  ];
  return (
    <Card>
      <CardContent className="p-6">
        <ul className="space-y-4">
          {events.map((e) => (
            <li key={e.label} className="flex items-start gap-3">
              <DocumentTextIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-secondary-400" />
              <div>
                <p className="text-sm font-medium text-secondary-800 dark:text-secondary-200">{e.label}</p>
                <p className="text-xs text-secondary-500">
                  {formatDate(e.at)}{e.by ? ` · by ${e.by}` : ''}
                </p>
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-xs text-secondary-400">A full activity feed arrives in a later directory sub-issue.</p>
      </CardContent>
    </Card>
  );
}
